/**
 * ClientPaymentSection — no raw fetch; all mutations come in via props (the
 * hook).
 */
import { formatTimestamp } from "../../lib/format-utils";
import { formatNZDCompact } from "@shared/nz-money";
import { cx } from "@shared/cx";
import badge from "../../lib/status-badge.module.css";
import styles from "./ClientPaymentSection.module.css";
import { SectionCard } from "../ui/SectionCard";
import { Banner } from "../ui/Banner";
import { Tooltip } from "../ui/Tooltip";
import { Button } from "../ui/Button";
import { ConfirmReceivedForm } from "./ConfirmReceivedForm";
import {
  derivePaymentActions,
  derivePaymentSummary,
} from "../../lib/payment-summary";
import {
  MUTATION_KEYS,
  type MutationRunner,
} from "../../lib/use-event-workspace";

interface ClientPaymentSectionProps {
  approval: {
    payment_status: string;
    deposit_percent: number | null;
    deposit_amount: number;
    balance_status: string;
    quote_notes?: string;
    quote_valid_days?: number;
    client_approval_token?: string;
    sent_to_client_at?: string;
    client_approved_at?: string;
    paid_at?: string;
    balance_paid_at?: string;
    client_confirmed_deposit_at?: string;
    client_confirmed_balance_at?: string;
    deposit_reminder_sent_at?: string;
    balance_invoiced_at?: string;
    balance_reminder_sent_at?: string;
    quote_reminder_sent_at?: string;
  } | null;
  paymentSummary: {
    quoteTotal: number;
    depositAmount: number;
    balanceAmount: number;
    depositStatus: string;
    balanceStatus: string;
  };
  hasLineItems: boolean;
  open: boolean;
  onToggle: () => void;
  sectionRef: React.RefObject<HTMLDivElement | null>;
  state: "active" | "complete" | "future";
  busy: boolean;
  mutations: MutationRunner;
  onSendDepositReminder: () => void;
  onSendBalanceInvoice: () => void;
  onSendBalanceReminder: () => void;
  onConfirmDeposit: (date: string) => void;
  onConfirmBalance: (date: string) => void;
  depositInvoiceUrl: string | null;
  balanceInvoiceUrl: string | null;
  invoicesStale: boolean;
  balanceInvoiceNeedsReissue: boolean;
  onRegenerate: () => void;
}

export function ClientPaymentSection({
  approval,
  paymentSummary,
  hasLineItems,
  open,
  onToggle,
  sectionRef,
  state,
  busy,
  mutations,
  onSendDepositReminder,
  onSendBalanceInvoice,
  onSendBalanceReminder,
  onConfirmDeposit,
  onConfirmBalance,
  depositInvoiceUrl,
  balanceInvoiceUrl,
  invoicesStale,
  balanceInvoiceNeedsReissue,
  onRegenerate,
}: ClientPaymentSectionProps) {
  const { depositDone, collapsedSummary } = derivePaymentSummary(
    paymentSummary,
    approval,
  );
  const actions = derivePaymentActions(paymentSummary, approval, {
    balanceInvoiceNeedsReissue,
  });

  // The invoice drafts re-render from the quote-generation path, so the
  // quote-summary staleness flag also covers them. Only cue staleness once a
  // draft actually exists to be out of date.
  const showStale =
    invoicesStale && Boolean(depositInvoiceUrl || balanceInvoiceUrl);

  const depositInvoicedAt =
    approval?.deposit_reminder_sent_at ?? approval?.sent_to_client_at ?? null;
  const balanceInvoicedAt =
    approval?.balance_reminder_sent_at ?? approval?.balance_invoiced_at ?? null;

  return (
    <SectionCard
      title="Client Payment"
      collapsible
      open={open}
      onToggle={onToggle}
      sectionRef={sectionRef}
      state={state}
      isStale={showStale}
      summary={
        showStale
          ? "Invoices are out of date, regenerate from Quoting tab"
          : collapsedSummary
      }
      headerAction={
        hasLineItems &&
        (depositInvoiceUrl || balanceInvoiceUrl) && (
          <span className={styles.headerLinks}>
            {depositInvoiceUrl && (
              <Button
                variant="link"
                size="sm"
                href={depositInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Deposit Invoice PDF
              </Button>
            )}
            {balanceInvoiceUrl && (
              <Button
                variant="link"
                size="sm"
                href={balanceInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Balance Invoice PDF
              </Button>
            )}
          </span>
        )
      }
    >
      <div className={styles.body}>
        {showStale && (
          <Banner variant="warning">
            The gear or quote has changed since these invoice PDFs were
            generated, so they are out of date.{" "}
            <button
              type="button"
              onClick={onRegenerate}
              className={styles.bannerLink}
            >
              Navigate to the Quoting tab
            </button>{" "}
            and re-generate the quote summary to update the invoices.
          </Banner>
        )}

        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>Quote total</span>
          <span className={styles.totalAmount}>
            {formatNZDCompact(paymentSummary.quoteTotal)}
          </span>
        </div>

        <div className={styles.divider} />

        <div className={styles.stage}>
          <div className={styles.stageHead}>
            <div>
              <span className={styles.stageLabel}>Deposit</span>
              {paymentSummary.depositAmount === 0 ? (
                <span
                  className={cx(badge.badge, styles.quietBadge, styles.badgeGap)}
                >
                  No deposit required
                </span>
              ) : paymentSummary.depositStatus === "paid" ? (
                <span className={cx(badge.badge, badge.green, styles.badgeGap)}>
                  Confirmed
                </span>
              ) : paymentSummary.depositStatus === "client_confirmed" ? (
                <span className={cx(badge.badge, badge.yellow, styles.badgeGap)}>
                  Client says paid, verify in bank
                </span>
              ) : (
                <span className={cx(badge.badge, badge.yellow, styles.badgeGap)}>
                  Waiting for client
                </span>
              )}
              {paymentSummary.depositStatus !== "paid" &&
                paymentSummary.depositStatus !== "client_confirmed" &&
                depositInvoicedAt && (
                  <p className={styles.sentAt}>
                    {approval?.deposit_reminder_sent_at
                      ? `Reminder sent ${formatTimestamp(approval.deposit_reminder_sent_at)}`
                      : `Invoiced ${formatTimestamp(depositInvoicedAt)}`}
                  </p>
                )}
              {approval?.client_confirmed_deposit_at &&
                paymentSummary.depositStatus !== "paid" && (
                  <p className={styles.sentAt}>
                    Client confirmed{" "}
                    {formatTimestamp(approval.client_confirmed_deposit_at)}
                  </p>
                )}
            </div>
            <span className={styles.amount}>
              {formatNZDCompact(paymentSummary.depositAmount)}
            </span>
          </div>
          {paymentSummary.depositAmount > 0 && (
            <div className={styles.actions}>
              {actions.sendDepositReminder && (
                <Button
                  onClick={onSendDepositReminder}
                  variant="outline"
                  loading={mutations.isPending(
                    MUTATION_KEYS.sendDepositReminder,
                  )}
                  disabled={busy}
                  className={styles.fullWidth}
                >
                  Send Deposit Reminder
                </Button>
              )}
              {approval?.paid_at ? (
                <p className={styles.receivedAt}>
                  Received {formatTimestamp(approval.paid_at)}
                </p>
              ) : actions.confirmDepositReceived ? (
                <ConfirmReceivedForm
                  dateLabel="Date received"
                  dateAriaLabel="Deposit date received"
                  confirmLabel="Confirm Received"
                  loading={mutations.isPending(
                    MUTATION_KEYS.confirmClientPayment("deposit"),
                  )}
                  disabled={busy}
                  onConfirm={onConfirmDeposit}
                />
              ) : null}
            </div>
          )}
        </div>

        <div className={styles.divider} />

        <div className={styles.stage}>
          <div className={styles.stageHead}>
            <div>
              <span className={styles.stageLabel}>
                <Tooltip content="Quote total minus the deposit amount.">
                  Remaining
                </Tooltip>
              </span>
              {paymentSummary.balanceStatus === "paid" ? (
                <span className={cx(badge.badge, badge.green, styles.badgeGap)}>
                  Confirmed
                </span>
              ) : paymentSummary.balanceStatus === "client_confirmed" ? (
                <span className={cx(badge.badge, badge.yellow, styles.badgeGap)}>
                  Client says paid, verify in bank
                </span>
              ) : paymentSummary.balanceStatus === "invoiced" ? (
                <span className={cx(badge.badge, badge.blue, styles.badgeGap)}>
                  Invoiced, waiting for client
                </span>
              ) : paymentSummary.depositStatus === "paid" ||
                paymentSummary.depositStatus === "client_confirmed" ? (
                <span
                  className={cx(badge.badge, styles.quietBadge, styles.badgeGap)}
                >
                  Not yet invoiced
                </span>
              ) : null}
              {paymentSummary.balanceStatus !== "paid" &&
                paymentSummary.balanceStatus !== "client_confirmed" &&
                balanceInvoicedAt && (
                  <p className={styles.sentAt}>
                    {approval?.balance_reminder_sent_at
                      ? `Reminder sent ${formatTimestamp(approval.balance_reminder_sent_at)}`
                      : `Invoiced ${formatTimestamp(balanceInvoicedAt)}`}
                  </p>
                )}
              {approval?.client_confirmed_balance_at &&
                paymentSummary.balanceStatus !== "paid" && (
                  <p className={styles.sentAt}>
                    Client confirmed{" "}
                    {formatTimestamp(approval.client_confirmed_balance_at)}
                  </p>
                )}
            </div>
            <span className={styles.amount}>
              {formatNZDCompact(paymentSummary.balanceAmount)}
            </span>
          </div>
          {depositDone && (
            <div className={styles.actions}>
              {actions.sendBalanceInvoice && (
                <Button
                  onClick={onSendBalanceInvoice}
                  loading={mutations.isPending(
                    MUTATION_KEYS.sendBalanceInvoice,
                  )}
                  disabled={busy}
                  className={styles.fullWidth}
                >
                  {actions.sendBalanceInvoiceLabel}
                </Button>
              )}
              {actions.sendBalanceReminder && (
                <Button
                  onClick={onSendBalanceReminder}
                  variant="outline"
                  loading={mutations.isPending(
                    MUTATION_KEYS.sendBalanceReminder,
                  )}
                  disabled={busy}
                  className={styles.fullWidth}
                >
                  Send Balance Reminder
                </Button>
              )}
              {approval?.balance_paid_at ? (
                <p className={styles.receivedAt}>
                  Received {formatTimestamp(approval.balance_paid_at)}
                </p>
              ) : actions.confirmBalanceReceived ? (
                <ConfirmReceivedForm
                  dateLabel="Date received"
                  dateAriaLabel="Balance date received"
                  confirmLabel="Confirm Received"
                  loading={mutations.isPending(
                    MUTATION_KEYS.confirmClientPayment("balance"),
                  )}
                  disabled={busy}
                  onConfirm={onConfirmBalance}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
